import React, { useState, ChangeEvent } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Query, Case } from 'generated/types'
import Loader from 'components/common/Loader'
import { DateTime } from 'luxon'
import {
  ListGroup,
  ListGroupItem,
  ListGroupItemHeading,
  ListGroupItemText,
  Row,
  Col,
} from 'reactstrap'
import Divider from 'components/common/Divider'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PrimaryButton from 'components/common/PrimaryButton'
import SearchBar from 'components/common/SearchBar'
import PageSubtitle from 'components/common/PageSubtitle'
import { editCaseRoute, caseRoute } from 'helpers/routing'
import { Badge } from 'components/common/Badge'
import { BadgeForStatus } from 'components/common/CaseBadges'
import LayoutTitle from 'teleporters/LayoutTitle'
import PageTitle from 'components/common/PageTitle'
import { DefaultDateRangePicker } from 'components/common/DateRangePicker'
import { useHistory } from 'react-router-dom'

export const CASES_QUERY = gql`
  query Cases {
    cases {
      id
      date
      status
      contactCount
      user {
        name
        email
      }
    }
  }
`

const StyledListGroupItem = styled(ListGroupItem)`
  background-color: #F5F6F8;
  padding: 15px;
  border-radius: 7px !important;
  margin-bottom: 10px;
  border: 0;
  cursor: pointer;
  box-shadow: 0 0 0 0.1rem transparent;
  transition: background-color 0.15s ease-in-out;
  transition: box-shadow 0.15s ease-in-out;

  &:hover {
    box-shadow: 0 0 0 0.1rem rgba(222,226,230,1);
    background-color: #F1F2F3;
    transition: background-color 0.15s ease-in-out;
    transition: box-shadow 0.15s ease-in-out;
  }
`

const StyledListGroupItemHeading = styled(ListGroupItemHeading)`
  font-family: Poppins;
  font-weight: 500;
  font-size: 12px;
  color: #939ABC;
  margin-bottom: 2px;
`

const StyledListGroupItemText = styled(ListGroupItemText)`
  font-family: Poppins;
  font-weight: 600;
  font-size: 16px;
  color: #242735;
  display: inline-block;
  margin-bottom: 0;
`

const StyledIcon = styled(FontAwesomeIcon)`
  margin-right: 8px;
  font-size: 11px;
`

const filterCasesByText = (cases?: Case[], searchText?: string): Case[] | undefined => {
  if (!searchText || !cases) {
    return cases
  }

  return cases.filter((caseItem) => {
    if (caseItem.user.name?.toLowerCase().includes(searchText)) {
      return true
    }

    const email = caseItem.user.email?.toLowerCase()
    if (!email) {
      return false
    }

    // if filter looks like email, check full email
    if (searchText.includes('@')) {
      return email.toLowerCase().includes(searchText)
    }

    // else check 'name' part of email to keep searches accurate
    // ex: typing 'ed' will match every .edu email without this
    return email.substring(0, email.indexOf('@')).includes(searchText)
  })
}

const filterCasesByDate = (cases?: Case[], startDate?: Date | null, endDate?: Date | null): Case[] | undefined => {
  if (!cases || (!startDate && !endDate)) {
    return cases
  }

  const startDateTime = startDate ? DateTime.fromJSDate(startDate).toLocal().startOf('day') : null
  const endDateTime = endDate ? DateTime.fromJSDate(endDate).toLocal().endOf('day') : null

  return cases.filter((caseItem) => {
    const caseDateTime = DateTime.fromISO(caseItem.date)

    if (startDateTime && startDateTime > caseDateTime) {
      return false
    }

    if (endDateTime && caseDateTime > endDateTime) {
      return false
    }

    return true
  })
}

export const TracingPage: React.FC = () => {
  const [searchFilter, setSearchFilter] = useState('')
  const [datesFilter, setDatesFilter] = useState<Array<Date | null>>([null, null])
  const { data, loading } = useQuery<Query>(CASES_QUERY)
  const history = useHistory()

  if (loading) {
    return (
      <Loader title="Loading tracing..." />
    )
  }

  let cases = data?.cases

  cases = filterCasesByText(cases, searchFilter)
  cases = filterCasesByDate(cases, ...datesFilter)

  cases = cases?.slice().sort((a, b) => b.date - a.date)

  return (
    <>
      <LayoutTitle.Source>
        <PageTitle>Contact Tracing</PageTitle>
      </LayoutTitle.Source>

      <Row>
        <Col xs="12" sm="12" md="12" lg="3">
          <PrimaryButton
            title="Open New Case"
            icon="clipboard"
            to={editCaseRoute(0)}
          />
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col xs="12" sm="12" md="4">
          <PageSubtitle>Case History</PageSubtitle>
        </Col>
      </Row>

      <Row>
        <Col className="mb-4" xs="12" sm="12" md="12" lg="3">
          <SearchBar
            value={searchFilter}
            onChange={(evt: ChangeEvent<HTMLInputElement>) => { setSearchFilter(evt.target.value.toLowerCase()) }}
          />
        </Col>
        <Col className="mb-4" xs="12" sm="12" md="12" lg="9">
          <DefaultDateRangePicker
            startDate={datesFilter[0]}
            endDate={datesFilter[1]}
            onClose={({ startDate, endDate }) => {
              setDatesFilter([startDate, endDate])
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs="12" sm="12" md="10" lg="12" xl="8">
          <ListGroup>
            { cases?.map((caseItem) => (
              <StyledListGroupItem
                onClick={() => history.push(caseRoute(caseItem.id))}
                color="success"
                key={caseItem.id}
              >
                <Row>
                  <Col>
                    <StyledListGroupItemHeading>Name</StyledListGroupItemHeading>
                    <StyledListGroupItemText>{ caseItem.user.name }</StyledListGroupItemText>
                  </Col>
                  <Col className="align-self-center" xs="3" sm="3" md="8" lg="8" xl="8">
                    <BadgeForStatus status={caseItem.status} pill />
                    <Badge pill><StyledIcon icon="calendar" />
                      { DateTime.fromISO(caseItem.date).toFormat('MMMM d') }
                    </Badge>
                    <Badge pill><StyledIcon icon="users" />
                      { caseItem.contactCount } Contact{ caseItem.contactCount !== 1 && 's'}
                    </Badge>
                  </Col>
                </Row>
              </StyledListGroupItem>
            )
            )}
          </ListGroup>
        </Col>
      </Row>
    </>
  )
}

export default TracingPage
